import { createPortal } from 'react-dom';
import { useToastNotificationContext } from '@sendible/design-system';
import { EVENTS, subscribe } from '@sendible/event-manager';
import type { MediaIdType, MediaType, composeTabs } from '@sendible/shared-state-bridge';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import VideoEditor from '@sendible/sendible360/src/assets/react/VideoThumbnail/index.js';
import { createThumbnailFromVideoAtOffset } from '@sendible/frontendv2/src/components/MediaSystem/utils';
import { useUploadsContext } from '@sendible/frontendv2/src/models/uploads/uploadsContext';
import { getThumbnailSupportedProfilesFromSelection } from '../../../../utils/videoThumbnailSupport/getThumbnailSupportedProfilesFromSelection';
import { COMPOSE_TABS } from '../../consts';
import { composeMediaInteractions } from '../../composeMediaInteractions';
import { useComposeBoxContext } from '../../composeBoxContext';

type FailedMediaEvent = {
  fileId?: string;
  mediaId?: MediaIdType;
  error: string;
};

type EditableVideoSettings = VideoSettings & {
  updatedMediaId?: string;
};

type VideoSettingsProps = {
  mediaId: MediaIdType;
  setShowVideoThumbnailSettings: (settings: { isOpen: boolean; mediaObject: MediaType | null }) => void;
};

export const VideoSettings = ({ mediaId, setShowVideoThumbnailSettings }: VideoSettingsProps) => {
  const { showToastNotification } = useToastNotificationContext();
  const { uploadThumbnail, updateThumbnailById } = useUploadsContext();
  const { activeComposeBoxTab, activePostType } = useComposeBoxContext();
  const { t } = useTranslation('validations');
  const attachedMediasOnActiveTab = composeMediaInteractions.getAttachedMedia(activeComposeBoxTab);
  const media = attachedMediasOnActiveTab ? attachedMediasOnActiveTab[mediaId] : null;

  const videoSettingRef = useRef<null | EditableVideoSettings>(null);
  const uploadingThumbnailRef = useRef<null | MediaType>(null);
  const uploadingThumbnailFileIdRef = useRef<string>('');
  const onErrorCallbackRef = useRef<null | (() => void)>(null);
  const tabsState = composeMediaInteractions.getTabsState() || {};
  const tabServices = Object.keys(tabsState) as composeTabs[];
  const profileListServices = tabsState[COMPOSE_TABS.GROUP]?.profileListServices || [];

  const selectedVideoSupportedServices = getThumbnailSupportedProfilesFromSelection(tabServices);
  const selectedProfileListSupportedServices = getThumbnailSupportedProfilesFromSelection(profileListServices);
  const isOriginalTab = activeComposeBoxTab === COMPOSE_TABS.MESSAGE;
  const services: string[] = useMemo(() => {
    if (isOriginalTab && selectedVideoSupportedServices.length > 0) {
      return [...new Set([...selectedVideoSupportedServices, ...selectedProfileListSupportedServices])];
    }

    if (activeComposeBoxTab === COMPOSE_TABS.GROUP) {
      return selectedProfileListSupportedServices;
    }

    if (!isOriginalTab && tabServices.length > 0) {
      return [activeComposeBoxTab];
    }

    return [];
  }, [activeComposeBoxTab, isOriginalTab, selectedVideoSupportedServices, selectedProfileListSupportedServices, tabServices.length]);

  useEffect(() => {
    const unsubscribeThumbnailUpload = subscribe(EVENTS.UPLOAD_READY, (readyMedia: MediaType) => {
      if (readyMedia.id === uploadingThumbnailRef.current?.id && videoSettingRef.current && media?.id) {
        composeMediaInteractions.replaceMedia(media?.id, readyMedia.id, activeComposeBoxTab);
        composeMediaInteractions.updateVideoSettings(readyMedia.id, activeComposeBoxTab, videoSettingRef.current);

        setShowVideoThumbnailSettings({ isOpen: false, mediaObject: null });
      }
    });

    const unsubscribeThumbnailUploadFailed = subscribe(EVENTS.UPLOAD_FAILED, (failedMedia: FailedMediaEvent) => {
      if (failedMedia.fileId === uploadingThumbnailFileIdRef.current) {
        showToastNotification({
          notificationText: failedMedia.error,
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });

        if (onErrorCallbackRef.current) {
          onErrorCallbackRef.current();
        }
      }
    });

    return () => {
      unsubscribeThumbnailUpload();
      unsubscribeThumbnailUploadFailed();
    };
  }, [media]);

  const saveVideoSettings = async (data: EditableVideoSettings, onError: () => void) => {
    const { videoSettings: prevVideoSettings } = media || {};
    const defaultVideoSettings: EditableVideoSettings = {
      category: '',
      frameInMiliseconds: 0,
      membersToNotify: [],
      title: '',
      youtubePrivacyStatus: 'public',
    };
    const dataMatchesPrev = JSON.stringify(prevVideoSettings) === JSON.stringify(data);
    const dataMatchesDefault = JSON.stringify(data) === JSON.stringify(defaultVideoSettings);
    const videoAtZeroSecondsWithoutPrevSettings = data.frameInMiliseconds === 0 && !prevVideoSettings;

    if (dataMatchesPrev || dataMatchesDefault || !media) {
      setShowVideoThumbnailSettings({ isOpen: false, mediaObject: null });

      return;
    }

    window.Backbone.trigger('auditor-event::video-details-changed', { link_title: data.title }, activeComposeBoxTab);

    const hasCustomThumbnailChanged = !!data.updatedMediaId && data.updatedMediaId !== media?.videoSettings?.updatedMediaId;
    const hasSliderChanged = data.frameInMiliseconds !== media?.videoSettings?.frameInMiliseconds && !videoAtZeroSecondsWithoutPrevSettings;

    if (hasCustomThumbnailChanged) {
      videoSettingRef.current = data;
      if (videoSettingRef.current && media?.id) {
        composeMediaInteractions.replaceMedia(media.id, Number(data.updatedMediaId), activeComposeBoxTab);
        composeMediaInteractions.deleteOrphanTwMedia(media.id);
        composeMediaInteractions.updateVideoSettings(Number(data.updatedMediaId), activeComposeBoxTab, videoSettingRef.current);
        setShowVideoThumbnailSettings({ isOpen: false, mediaObject: null });
      }
    } else if (hasSliderChanged) {
      const thumbnailFile = await createThumbnailFromVideoAtOffset(media.originalUrl, data.frameInMiliseconds);

      videoSettingRef.current = data;
      onErrorCallbackRef.current = onError;

      const uploadedThumbnailId = await uploadThumbnail({
        file: thumbnailFile as File,
        onUploadStarted: () => {
          const generatedFileId = Math.random().toString();

          uploadingThumbnailFileIdRef.current = generatedFileId;

          return generatedFileId;
        },
      });

      if (!uploadedThumbnailId) {
        onError();

        return;
      }

      const uploadingThumbnail = await updateThumbnailById({
        id: Number(uploadedThumbnailId),
        originalMediaId: media.id,
        fileId: uploadingThumbnailFileIdRef.current,
      });

      if (!uploadingThumbnail) {
        onError();

        return;
      }

      uploadingThumbnailRef.current = uploadingThumbnail;
    } else {
      composeMediaInteractions.updateVideoSettings(media.id, activeComposeBoxTab, data);

      setShowVideoThumbnailSettings({ isOpen: false, mediaObject: null });
    }
  };

  return (
    <div>
      {createPortal(
        <VideoEditor
          src={media?.originalUrl}
          closeModal={() => setShowVideoThumbnailSettings({ isOpen: false, mediaObject: null })}
          saveClicked={saveVideoSettings}
          videoSize={media?.metadata?.size || 0}
          isOriginalTab={isOriginalTab}
          services={services}
          title={media?.videoSettings?.title || ''}
          category={media?.videoSettings?.category || ''}
          membersToNotify={media?.videoSettings?.membersToNotify || []}
          frameInMiliseconds={media?.videoSettings?.frameInMiliseconds || 0}
          youtubePrivacyStatus={media?.videoSettings?.youtubePrivacyStatus || 'public'}
          postType={activePostType}
          originalMediaId={media?.id}
          t={t}
        />,
        document.getElementById('videoThumbnailEditor') as HTMLElement
      )}
    </div>
  );
};
